/**
 * Milestone 2 Arcade loop — run spine, soothing, shelf/toys, queue (2a–2d).
 */

import { WebAudioAudio } from '@meowntain/audio';
import {
  ARCADE_SHELF,
  BEDROOM_DEFAULT,
  BEDROOM_SURFACE_Y,
  M1_ARCHETYPES,
  QUEUE_HIT_RADIUS,
  arcadeQueueSlots,
  bedTopY,
  getArchetype,
  isPointInPlayfield,
  isPointInShelf,
  shelfLootAtPoint,
} from '@meowntain/content';
import {
  ARCADE_RUN_CONFIG,
  RunController,
  StrokeTracker,
  computeScore,
  findCatAtPoint,
  hitTestWaitingCat,
  stackHeight,
} from '@meowntain/core';
import type { WakeReason } from '@meowntain/core';
import { Canvas2DRenderer } from '@meowntain/render';
import { RapierSimulation } from '@meowntain/sim';

const FIXED_DT = 1 / 60;
const MAX_FRAME_TIME = 0.1;

const ARCHETYPE_MAP = new Map(M1_ARCHETYPES.map((a) => [a.id, a]));

const BED_SURFACE_Y = bedTopY(BEDROOM_DEFAULT.playfieldHeight) - BEDROOM_SURFACE_Y * 0.4;
const QUEUE_SLOTS = arcadeQueueSlots();

const WAKE_COPY: Record<WakeReason, string> = {
  hungry: 'The sleeper wakes up — hungry.',
  bathroom: 'The sleeper wakes up — bathroom.',
  phone: 'The sleeper wakes up — phone.',
};

export interface GameHandles {
  start: () => Promise<void>;
  destroy: () => void;
}

interface PointerSession {
  catId: string;
  archetypeId: string;
  strokeTracker: StrokeTracker | null;
  holdEngaged: boolean;
  comfortHumPlaying: boolean;
  startX: number;
  startY: number;
  moved: boolean;
  downMs: number;
}

export async function bootGame(host: HTMLElement, hud: HudElements): Promise<GameHandles> {
  const renderer = new Canvas2DRenderer();
  await renderer.init(host);

  const sim = new RapierSimulation();
  await sim.init();

  const audio = new WebAudioAudio();
  const run = new RunController();
  run.registerArchetypes(M1_ARCHETYPES);

  let placedCount = 0;
  let maxHeightSeen = 0;
  let running = true;
  let lastTimeMs = performance.now();
  let acc = 0;
  let snoreEnabled = false;
  let catnipArmed = false;
  let shelfHovering = false;
  let pointer: PointerSession | null = null;
  let heldFromQueue = false;
  let heldX = 0;
  let heldY = 0;

  const ensureAudio = async (): Promise<void> => {
    if (!snoreEnabled) {
      await audio.start();
      audio.setSnoreIntensity(0.5);
      snoreEnabled = true;
    }
  };

  const canvasToWorld = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = host.getBoundingClientRect();
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;
    const worldW = BEDROOM_DEFAULT.bedWidth;
    const worldH = BEDROOM_DEFAULT.playfieldHeight;
    const scale = Math.min(rect.width / worldW, rect.height / worldH);
    const offsetX = (rect.width - worldW * scale) / 2;
    const offsetY = (rect.height - worldH * scale) / 2;
    const x = (screenX - offsetX) / scale;
    const y = (screenY - offsetY) / scale;
    if (!isPointInPlayfield(x, y)) return null;
    return { x, y };
  };

  const canDropAt = (y: number): boolean => y < bedTopY(BEDROOM_DEFAULT.playfieldHeight) - 40;

  const stackMetrics = (): { heightAboveBed: number; topY: number } => {
    const states = sim.getCatStates();
    let topY = BEDROOM_DEFAULT.playfieldHeight;
    for (const s of states) {
      if (s.y < topY) topY = s.y;
    }
    return { heightAboveBed: stackHeight(BED_SURFACE_Y, topY), topY };
  };

  const placeHeldCat = (point: { x: number; y: number }): void => {
    const archetypeId = run.confirmHeldPlacement(stackMetrics().heightAboveBed, placedCount);
    if (!archetypeId) return;
    sim.spawnCat(archetypeId, point);
    placedCount += 1;
    heldFromQueue = false;
    updateHud();
  };

  const updateShelfHover = (point: { x: number; y: number } | null): void => {
    const rs = run.getState();
    const hovering = !!point && rs.shelfReachable && isPointInShelf(ARCADE_SHELF, point.x, point.y);
    if (hovering !== shelfHovering) {
      shelfHovering = hovering;
      run.pawHoverShelf(hovering);
      if (hovering) audio.emit('toy_glint');
    }
  };

  const endPointer = (): void => {
    if (pointer?.comfortHumPlaying) {
      audio.emit('comfort_hum_release');
    }
    run.endActiveHold();
    pointer = null;
    if (heldFromQueue) {
      run.cancelHeldPickup();
      heldFromQueue = false;
    }
  };

  const tryShelfGrab = (point: { x: number; y: number }): boolean => {
    const rs = run.getState();
    if (!rs.shelfReachable) return false;
    const loot = shelfLootAtPoint(ARCADE_SHELF, point.x, point.y);
    if (!loot) return false;
    const result = run.grabShelfLoot(loot, sim.getCatStates());
    if (result === 'treat') {
      audio.emit('treat_applied');
      updateHud();
      return true;
    }
    if (result === 'toy') {
      audio.emit('toy_curious');
      updateHud();
      return true;
    }
    return true;
  };

  const onPointerDown = async (ev: PointerEvent): Promise<void> => {
    if (!run.isPlaying()) return;
    await ensureAudio();
    const point = canvasToWorld(ev.clientX, ev.clientY);
    if (!point) return;
    host.setPointerCapture(ev.pointerId);

    if (catnipArmed) {
      const count = run.useCatnip(point.x, point.y, sim.getCatStates());
      catnipArmed = false;
      hud.catnipButton.classList.remove('pantry-btn--armed');
      if (count > 0) audio.emit('catnip_applied');
      updateHud();
      return;
    }

    if (tryShelfGrab(point)) return;

    const waiting = hitTestWaitingCat(
      run.getWaitingCats(),
      QUEUE_SLOTS,
      point.x,
      point.y,
      QUEUE_HIT_RADIUS,
    );
    if (waiting) {
      const archetypeId = run.pickupWaitingCat(waiting.queueId);
      if (archetypeId) {
        heldFromQueue = true;
        heldX = point.x;
        heldY = point.y;
        pointer = null;
      }
      return;
    }

    const hit = findCatAtPoint(sim.getCatStates(), ARCHETYPE_MAP, point.x, point.y);
    if (hit) {
      const archetype = getArchetype(hit.archetypeId);
      const longAxis = Math.max(archetype.width, archetype.height);
      const strokeTracker =
        archetype.soothingVerb === 'stroke'
          ? new StrokeTracker(hit.catId, hit.x, hit.y, hit.rotation, longAxis, ARCADE_RUN_CONFIG)
          : null;
      pointer = {
        catId: hit.catId,
        archetypeId: hit.archetypeId,
        strokeTracker,
        holdEngaged: archetype.soothingVerb === 'hold',
        comfortHumPlaying: false,
        startX: point.x,
        startY: point.y,
        moved: false,
        downMs: performance.now(),
      };
      if (pointer.holdEngaged) {
        run.beginComfortHold(hit.catId);
        audio.emit('comfort_hum_engage');
        pointer.comfortHumPlaying = true;
      }
      return;
    }

    pointer = null;
  };

  const onPointerMove = (ev: PointerEvent): void => {
    const point = canvasToWorld(ev.clientX, ev.clientY);
    if (heldFromQueue && point) {
      heldX = point.x;
      heldY = point.y;
    }
    if (!pointer && !heldFromQueue) updateShelfHover(point);

    if (!run.isPlaying() || !pointer) return;
    if (!point) return;

    const dist = Math.hypot(point.x - pointer.startX, point.y - pointer.startY);
    if (dist > ARCADE_RUN_CONFIG.holdMotionThresholdPx) pointer.moved = true;

    const states = sim.getCatStates();
    const hit = findCatAtPoint(states, ARCHETYPE_MAP, point.x, point.y);
    if (!hit || hit.catId !== pointer.catId) return;

    const archetype = getArchetype(pointer.archetypeId);
    const nowMs = performance.now();

    if (pointer.strokeTracker && pointer.moved) {
      if (pointer.holdEngaged && pointer.comfortHumPlaying) {
        audio.emit('comfort_hum_release');
        pointer.comfortHumPlaying = false;
        run.endActiveHold();
        pointer.holdEngaged = false;
      }
      if (pointer.strokeTracker.move(point.x, point.y, nowMs)) {
        if (run.registerStroke(pointer.catId, states)) {
          audio.emit('fur_ruffle');
        }
      }
      return;
    }

    if (
      !pointer.holdEngaged &&
      archetype.soothingVerb === 'stroke' &&
      !pointer.moved &&
      nowMs - pointer.downMs >= ARCADE_RUN_CONFIG.holdEngageDelayMs
    ) {
      pointer.holdEngaged = true;
      run.beginComfortHold(pointer.catId);
      audio.emit('comfort_hum_engage');
      pointer.comfortHumPlaying = true;
    }
  };

  const onPointerUp = async (ev: PointerEvent): Promise<void> => {
    try {
      host.releasePointerCapture(ev.pointerId);
    } catch {
      // Already released.
    }
    updateShelfHover(null);
    run.pawHoverShelf(false);
    shelfHovering = false;

    if (!run.isPlaying()) {
      endPointer();
      return;
    }

    const point = canvasToWorld(ev.clientX, ev.clientY);
    if (pointer) {
      endPointer();
      updateHud();
      return;
    }

    if (heldFromQueue && point && canDropAt(point.y)) {
      await ensureAudio();
      placeHeldCat(point);
      return;
    }
    if (heldFromQueue) {
      run.cancelHeldPickup();
      heldFromQueue = false;
      updateHud();
    }
  };

  host.addEventListener('pointerdown', (ev) => void onPointerDown(ev));
  host.addEventListener('pointermove', onPointerMove);
  host.addEventListener('pointerup', (ev) => void onPointerUp(ev));
  host.addEventListener('pointercancel', () => {
    updateShelfHover(null);
    run.pawHoverShelf(false);
    endPointer();
  });

  const onTreat = async (): Promise<void> => {
    if (!run.isPlaying()) return;
    await ensureAudio();
    const applied = run.useTreat(null, sim.getCatStates());
    if (applied) {
      audio.emit('treat_applied');
      updateHud();
    }
  };
  const onTreatClick = (ev: Event): void => {
    ev.stopPropagation();
    void onTreat();
  };
  hud.treatButton.addEventListener('click', onTreatClick);

  const onCatnipClick = (ev: Event): void => {
    ev.stopPropagation();
    if (!run.isPlaying() || run.getState().catnipRemaining <= 0) return;
    catnipArmed = !catnipArmed;
    hud.catnipButton.classList.toggle('pantry-btn--armed', catnipArmed);
    updateHint();
  };
  hud.catnipButton.addEventListener('click', onCatnipClick);

  const onHideToy = (ev: Event): void => {
    ev.stopPropagation();
    if (!run.isToyExposed()) return;
    run.hideToyOnShelf();
    audio.emit('cat_landed_soft');
    updateHud();
  };
  hud.hideToyButton.addEventListener('click', onHideToy);

  const setMeterFill = (el: HTMLElement, ratio: number): void => {
    const fill = el.querySelector<HTMLElement>('.meter-fill');
    if (fill) fill.style.width = `${Math.round(Math.max(0, Math.min(1, ratio)) * 100)}%`;
  };

  const updateHint = (): void => {
    const rs = run.getState();
    if (rs.phase !== 'playing') return;
    if (catnipArmed) {
      hud.hint.textContent = 'tap the stack to scatter catnip';
      return;
    }
    if (run.isToyExposed()) {
      hud.hint.textContent = 'toy out — hide it or calm cats before they bolt';
      return;
    }
    if (rs.shelfReachable) {
      hud.hint.textContent = 'paw reach! tap T (treat) or ? (toy) on the shelf';
      return;
    }
    hud.hint.textContent = 'pick a waiting cat · drag onto the stack · soothe or reach the shelf';
  };

  const updateHud = (): void => {
    const { heightAboveBed } = stackMetrics();
    if (heightAboveBed > maxHeightSeen) maxHeightSeen = heightAboveBed;

    const rs = run.getState();
    hud.count.textContent = String(placedCount);
    hud.height.textContent = `${Math.round(maxHeightSeen)}`;
    hud.score.textContent = String(computeScore(placedCount, maxHeightSeen));
    hud.treats.textContent = String(rs.treatsRemaining);
    hud.catnip.textContent = String(rs.catnipRemaining);
    setMeterFill(hud.personMeter, rs.personRestlessness);
    setMeterFill(hud.catMeter, run.getMaxCatRestlessness());

    hud.hideToyButton.hidden = !run.isToyExposed();

    const phase = rs.phase;
    if (phase === 'playing') {
      hud.wakeOverlay.hidden = true;
      updateHint();
      hud.treatButton.disabled = rs.treatsRemaining <= 0;
      hud.catnipButton.disabled = rs.catnipRemaining <= 0;
    } else if (phase === 'wake_telegraph') {
      hud.wakeOverlay.hidden = false;
      hud.wakeMessage.textContent = 'Waking up…';
      hud.hint.textContent = '';
      hud.treatButton.disabled = true;
      hud.catnipButton.disabled = true;
      hud.hideToyButton.hidden = true;
      catnipArmed = false;
      hud.catnipButton.classList.remove('pantry-btn--armed');
    } else {
      hud.wakeOverlay.hidden = false;
      const reason = rs.wakeReason ?? 'hungry';
      hud.wakeMessage.textContent = `${WAKE_COPY[reason]} Score ${computeScore(placedCount, maxHeightSeen)}.`;
      hud.hint.textContent = 'reload to play again';
      hud.treatButton.disabled = true;
      hud.catnipButton.disabled = true;
      hud.hideToyButton.hidden = true;
    }
  };

  const syncVisuals = (): void => {
    const rs = run.getState();
    const soothing = new Map<
      string,
      { bliss: number; holdActive: boolean; toyCurious?: boolean }
    >();
    for (const [id, entry] of run.getCatGameplay()) {
      if (entry.leaving) continue;
      soothing.set(id, {
        bliss: entry.bliss,
        holdActive: entry.holdActive,
        toyCurious: entry.toyCurious,
      });
    }
    renderer.updateCatSoothing(soothing);
    renderer.updateShelfVisual({
      reachable: rs.shelfReachable,
      glint: rs.toyStage === 'glint',
      toyOnShelf: rs.toyOnShelf,
      toyExposed: run.isToyExposed(),
    });
    renderer.updateWaitingCats(
      run.getWaitingCats().map((c) => ({
        queueId: c.queueId,
        archetypeId: c.archetypeId,
        slotIndex: c.slotIndex,
        patience: c.patience,
        behavior: c.behavior,
        behaviorPhase: c.behaviorPhase,
      })),
      QUEUE_SLOTS,
      heldFromQueue && run.heldArchetypeId()
        ? { archetypeId: run.heldArchetypeId() as string, x: heldX, y: heldY }
        : null,
    );
  };

  const tick = (nowMs: number): void => {
    if (!running) return;
    const frameTime = Math.min(MAX_FRAME_TIME, (nowMs - lastTimeMs) / 1000);
    lastTimeMs = nowMs;
    acc += frameTime;

    while (acc >= FIXED_DT) {
      sim.step(FIXED_DT);
      const events = sim.drainImpactEvents();
      const states = sim.getCatStates();
      const { heightAboveBed } = stackMetrics();
      run.updateShelfReach(states, ARCADE_SHELF, BED_SURFACE_Y, heightAboveBed, placedCount);
      const tickResult = run.tick(FIXED_DT, states, events, heightAboveBed, placedCount);
      if (tickResult.toyCollapse) audio.emit('toy_alert');
      if (tickResult.floorToyStarted) audio.emit('toy_curious');
      for (const catId of tickResult.removedCatIds) {
        sim.removeCat(catId);
      }

      for (const ev of events) {
        if (ev.impulse > 2.5) audio.emit('cat_landed_thud');
        else if (ev.impulse > 0.6) audio.emit('cat_impact_on_cat');
        else audio.emit('cat_landed_soft');
      }
      acc -= FIXED_DT;
    }

    const rs = run.getState();
    audio.setPurrIntensity(rs.purrField);

    renderer.updateCats(sim.getCatStates());
    syncVisuals();
    renderer.render();
    updateHud();

    requestAnimationFrame(tick);
  };

  const handles: GameHandles = {
    start: async (): Promise<void> => {
      lastTimeMs = performance.now();
      updateHud();
      requestAnimationFrame(tick);
    },
    destroy: (): void => {
      running = false;
      endPointer();
      hud.treatButton.removeEventListener('click', onTreatClick);
      hud.catnipButton.removeEventListener('click', onCatnipClick);
      hud.hideToyButton.removeEventListener('click', onHideToy);
      renderer.destroy();
      sim.destroy();
      audio.destroy();
    },
  };
  return handles;
}

export interface HudElements {
  count: HTMLElement;
  height: HTMLElement;
  score: HTMLElement;
  personMeter: HTMLElement;
  catMeter: HTMLElement;
  treats: HTMLElement;
  catnip: HTMLElement;
  treatButton: HTMLButtonElement;
  catnipButton: HTMLButtonElement;
  hideToyButton: HTMLButtonElement;
  wakeOverlay: HTMLElement;
  wakeMessage: HTMLElement;
  hint: HTMLElement;
}
